import ApiService from "api/ApiService";
import ApiKey from "models/ApiKey";
import { InferenceConnectionInfo } from "models/InferenceConnectionInfo";
import { DeploymentMode } from "models/DeploymentMode";
import { InferenceRequestLog } from "models/InferenceRequestLog";
import ProjectData from "pages/Project/ProjectData";
import { ApiCommonResponse } from "api/ApiCommonResponse";
import { ProjectInfo } from "models/ProjectInfo";
import { Interval } from "models/Interval";
import { ProjectDeploymentInfo } from "models/ProjectDeploymentInfo";
import { convertDateTimeStringsToEventData } from "components/TimeIntervalPicker/TimeIntervalPicker";
import { getEnumByEnumValue } from "utils/enum";

interface APIProjectInfo {
  id: string;
  user_owner_id: number;
  name: string;
  project_status: number;
  last_request_timestamp: string;
  created_timestamp: string;
  updated_timestamp: string;
}

type UserProjectsResponse = ApiCommonResponse & {
  projects: APIProjectInfo[];
};

type NewProjectResponse = ApiCommonResponse & {
  project_id?: string;
};

type ProjectDataResponse = ApiCommonResponse & {
  project: ProjectData;
};

type ProjectKeysResponse = ApiCommonResponse & {
  keys: ApiKey[];
  message?: string;
};

type NewKeyResponse = ApiCommonResponse & {
  key?: ApiKey;
  plain?: string;
};

type GetProjectInfoResponse = ApiCommonResponse & {
  info: ProjectInfo;
};

type GetProjectDeploymentInfoResponse = ApiCommonResponse & {
  config: InferenceConnectionInfo;
  logs: InferenceRequestLog[];
  info: ProjectDeploymentInfo;
};

export default class ProjectService {
  private static getProjectUrl(): string {
    return "api/projects";
  }

  public static async getUserProjects(): Promise<UserProjectsResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}`,
          method: "GET",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async newProject(
    name: string,
    current_cost: string,
    use_case: string,
    current_revenue: string,
    current_deployment: string,
    product_info: string[],
    non_use_timings: Interval[],
    model_type: string,
    batch_size: string,
    input_shape: string,
    input_dtype: string,
    output_shape: string,
    output_dtype: string,
  ): Promise<NewProjectResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}`,
          method: "POST",
          data: {
            name,
            current_cost,
            use_case,
            current_revenue,
            current_deployment,
            product_info,
            non_use_timings,
            model_type,
            batch_size,
            input_shape,
            input_dtype,
            output_shape,
            output_dtype,
          },
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async projectCreationCompleted(
    proj_id: string,
  ): Promise<ApiCommonResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${proj_id}/creation_complete`,
          method: "POST",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async getProject(
    proj_id: string,
  ): Promise<ProjectDataResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${proj_id}`,
          method: "GET",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async deleteProject(
    project_id: string,
  ): Promise<UserProjectsResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${project_id}`,
          method: "DELETE",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // API Keys

  public static async getProjectKeys(
    proj_id: string,
  ): Promise<ProjectKeysResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${proj_id}/keys`,
          method: "GET",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async newProjectKey(
    proj_id: string,
    name: string,
    validity_period: number, // days
  ): Promise<NewKeyResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${proj_id}/keys`,
          method: "POST",
          data: {
            name,
            validity_period,
          },
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async deleteProjectKey(
    proj_id: string,
    key_id: number,
  ): Promise<NewKeyResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${proj_id}/keys/${key_id}`,
          method: "DELETE",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // Project Info Tab

  public static async getProjectInfo(
    proj_id: string,
  ): Promise<GetProjectInfoResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${proj_id}/info`,
          method: "GET",
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private static deserializeDeploymentInfo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
  ): GetProjectDeploymentInfoResponse {
    return {
      ...data,
      info: {
        ...data.info,
        current_mode: getEnumByEnumValue(
          DeploymentMode,
          data.info.current_mode,
        ),
        desired_mode: getEnumByEnumValue(
          DeploymentMode,
          data.info.desired_mode,
        ),
        production: {
          ...data.info.production,
          // non_use_timings: convertDateTimeStringsToEventData(
          //   data.info.production.non_use_timings,
          // ),
        },
      },
    };
  }

  // Project Inference Tab
  public static async getProjectDeploymentInfo(
    proj_id: string,
  ): Promise<GetProjectDeploymentInfoResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${proj_id}/inference_info`,
          method: "GET",
        },
        true,
      );
      return ProjectService.deserializeDeploymentInfo(response);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public static async changeDeploymentMode(
    proj_id: string,
    mode: DeploymentMode,
    non_use_timings?: Interval[],
    instance_type_id?: number,
  ): Promise<ApiCommonResponse> {
    try {
      const response = await ApiService.request(
        {
          url: `${this.getProjectUrl()}/${proj_id}/mode`,
          method: "POST",
          data: {
            mode,
            non_use_timings,
            instance_type_id,
          },
        },
        true,
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
